import { useMemo } from 'react'
import { useMetaData } from './useMetaData.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useNotifications — aggregates unread/recent activity directed at the
 * current user into a single sorted list for the 🔔 notification center.
 *
 * Source streams:
 *   - DMs: messages in meta.dms[threadKey] authored by someone else
 *   - @mentions: replies in meta.comments[*].replies that contain
 *     @<currentUser> and are authored by someone else
 *
 * Per-item seen state comes from uiStore:
 *   - dmSeen[threadKey] = count of messages already viewed in that thread
 *   - mentionSeen[replyId] = true when the user has acknowledged that mention
 *
 * Returns:
 *   items       — sorted newest-first, capped at 30 for UI sanity
 *   unreadCount — items where seen === false
 *   markAllRead — helper to flag every currently-returned item as seen
 */
export function useNotifications() {
  const currentUser = useUIStore((s) => s.currentUser)
  const dmSeen = useUIStore((s) => s.dmSeen)
  const mentionSeen = useUIStore((s) => s.mentionSeen)
  const markAllMentionsSeen = useUIStore((s) => s.markAllMentionsSeen)
  const markDmSeen = useUIStore((s) => s.markDmSeen)
  const { data: meta } = useMetaData()

  const items = useMemo(() => {
    if (!currentUser || !meta) return []
    const out = []

    // DMs — walk each thread the current user is a participant in
    for (const [threadKey, thread] of Object.entries(meta.dms || {})) {
      if (!(thread?.participants || []).includes(currentUser)) continue
      const msgs = thread.messages || []
      const seen = dmSeen[threadKey] || 0
      for (let i = 0; i < msgs.length; i++) {
        const m = msgs[i]
        if (m.author === currentUser) continue
        const wasSeen = i < seen
        const otherParticipants = (thread.participants || []).filter(
          (p) => p !== currentUser
        )
        const label =
          otherParticipants.length === 1
            ? otherParticipants[0]
            : otherParticipants.slice(0, 2).join(', ') +
              (otherParticipants.length > 2
                ? ` +${otherParticipants.length - 2}`
                : '')
        out.push({
          id: `dm-${threadKey}-${m.id}`,
          kind: 'dm',
          threadKey,
          otherParticipants,
          author: m.author,
          text: m.text,
          at: m.at,
          label,
          seen: wasSeen,
        })
      }
    }

    // @mentions — walk every reply that mentions the current user
    const mentionRe = new RegExp(`@${escapeRegex(currentUser)}\\b`, 'i')
    for (const thread of meta.comments || []) {
      for (const reply of thread.replies || []) {
        if (reply.author === currentUser) continue
        if (!mentionRe.test(reply.text || '')) continue
        out.push({
          id: `mention-${reply.id}`,
          kind: 'mention',
          replyId: reply.id,
          author: reply.author,
          text: reply.text,
          at: reply.at,
          contextLabel: thread.contextLabel,
          contextType: thread.contextType,
          seen: Boolean(mentionSeen[reply.id]),
        })
      }
    }

    // Newest first; cap at 30 so the panel doesn't grow unbounded
    return out
      .sort((a, b) => (b.at || '').localeCompare(a.at || ''))
      .slice(0, 30)
  }, [meta, currentUser, dmSeen, mentionSeen])

  const unreadCount = items.filter((i) => !i.seen).length

  const markAllRead = () => {
    // Bring every DM thread up to its current length
    for (const [threadKey, thread] of Object.entries(meta?.dms || {})) {
      if (!(thread?.participants || []).includes(currentUser)) continue
      markDmSeen(threadKey, (thread.messages || []).length)
    }
    // Mark every currently-shown mention as seen
    const mentionIds = items
      .filter((i) => i.kind === 'mention')
      .map((i) => i.replyId)
    markAllMentionsSeen(mentionIds)
  }

  return { items, unreadCount, markAllRead }
}

function escapeRegex(s) {
  return (s || '').replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
}
